import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, HandHeart, MessageCircle, Heart } from "lucide-react";

const EMOJI = { like: "👍", love: "❤️", pray: "🙏", amen: "🙌", haha: "😂", wow: "😮", sad: "😢" };

const text = (n) => {
  if (n.type === "comment") return <>commented: <span style={{ color: "var(--ink-2)" }}>"{n.detail}"</span></>;
  if (n.type === "pray") return <>prayed for your request</>;
  return <>reacted {EMOJI[n.detail] || ""} to your post</>;
};
const ICON = { comment: MessageCircle, pray: HandHeart, react: Heart };

export default function NotificationBell({ testid = "notif-bell" }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/social/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("social-notifs-changed", load);
    return () => { clearInterval(t); window.removeEventListener("social-notifs-changed", load); };
  }, [load]);
  const changed = () => window.dispatchEvent(new Event("social-notifs-changed"));
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) await api.post(`/social/notifications/${n.id}/read`).catch(() => {});
    changed();
    navigate(`/social?post=${n.post_id}`);
  };
  const readAll = async () => { await api.post("/social/notifications/read-all"); changed(); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen(!open); if (!open) load(); }} className="relative p-2 rounded-full hover:bg-[var(--surface-hover)] transition-colors" style={{ color: "var(--ink-2)" }} aria-label="Notifications" data-testid={testid}>
        <Bell size={18} />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full text-[0.6rem] font-semibold flex items-center justify-center" style={{ background: "#EC4899", color: "#FFF" }} data-testid="notif-count">{data.unread > 9 ? "9+" : data.unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[26rem] overflow-y-auto rounded-lg shadow-2xl z-50" style={{ background: "var(--bg-deep)", border: "1px solid var(--border-strong)" }} data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
            <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>Notifications</span>
            {data.unread > 0 && <button onClick={readAll} className="text-xs" style={{ color: "var(--gold)" }} data-testid="notif-read-all">Mark all read</button>}
          </div>
          {data.items.length === 0 && <p className="px-4 py-6 text-sm text-center" style={{ color: "var(--muted)" }} data-testid="notif-empty">Nothing yet. Reactions, comments and prayers on your posts show up here.</p>}
          {data.items.map((n) => {
            const Icon = ICON[n.type] || Heart;
            return (
              <button key={n.id} onClick={() => openItem(n)} className="w-full text-left px-4 py-3 flex gap-3 hover:bg-[var(--surface-hover)] transition-colors" style={{ background: n.read ? "transparent" : "rgba(230,184,92,0.06)" }} data-testid={`notif-item-${n.id}`}>
                <Icon size={15} className="mt-0.5 shrink-0" style={{ color: n.type === "pray" ? "#EC4899" : "var(--gold)" }} />
                <span className="min-w-0 text-xs leading-snug" style={{ color: "var(--muted)" }}>
                  <span className="font-medium" style={{ color: "var(--ink)" }}>{n.actor_name}</span> {text(n)}
                  <span className="block mt-0.5 truncate">on "{n.post_snippet}" · {new Date(n.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                </span>
                {!n.read && <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: "#EC4899" }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
